import "@/global.css";
import { useAuth } from "@/src/contexts/auth.context";
import { markSessionAlive } from "@/src/services/api/auth.api";
import { clearSessionCache } from "@/src/services/blocks/session";
import {
  finishLogin,
  finishSocialLogin,
  getSsoFlow,
} from "@/src/services/blocks/sso";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, Pressable, Text, View } from "react-native";

/**
 * The single place the authorization code is exchanged, for BOTH flows.
 *
 * The hosted `blocks-oidc` flow redirects straight here (`SSO_CALLBACK_PATH` in
 * `src/services/blocks/sso.ts`). The social flow — the one the app actually uses
 * — is registered against `/login` on the provider, so it lands there and
 * `use-login.ts` forwards it here rather than growing a second exchange.
 *
 * Blocks sends the browser here with `?code&state` (or `?error`). All this screen
 * does is exchange the code for the session cookie and hand off; `AuthProvider`
 * is the single authority for where an authenticated user lands, so on success we
 * redirect to `/` and let it route (see the comment in `app/index.tsx`).
 *
 * On native the code/state never reach this route — `startLogin` reads them off
 * the auth-session result and calls `finishLogin` directly. This screen is the
 * web leg of the flow.
 */
export default function AuthCallback() {
  const router = useRouter();
  const { t } = useTranslation();
  const { refreshAuth } = useAuth();
  const params = useLocalSearchParams<{
    code?: string;
    state?: string;
    error?: string;
    error_description?: string;
  }>();

  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // The authorization code is SINGLE-USE. React 18 runs effects twice in dev,
  // and any re-render would otherwise retry the exchange and fail with an
  // already-redeemed code — so the exchange is fired exactly once.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const { code, state, error: oauthError, error_description } = params;

    if (oauthError) {
      console.error("SSO callback error from provider:", oauthError, error_description);
      setError(t("ERROR_SSO_SIGNIN_FAILED"));
      return;
    }
    if (typeof code !== "string" || typeof state !== "string") {
      console.error("SSO callback missing code/state:", params);
      setError(t("ERROR_SSO_SIGNIN_FAILED"));
      return;
    }

    // The two flows have different exchange endpoints and each rejects the
    // other's state, so finish the one that actually started. `getSsoFlow()`
    // defaults to "social" — the configured provider and the only flow reachable
    // from the login screen.
    const exchange =
      getSsoFlow() === "oidc" ? finishLogin : finishSocialLogin;

    exchange(code, state)
      .then(async () => {
        // Drop any cached /iam/me + org resolution from a previous session so
        // AuthProvider reads the new one.
        clearSessionCache();

        // The web leg of SSO ends here, so this is where the new session gets
        // its run marker. The "Remember me" preference itself was persisted on
        // the login screen before the redirect — see `OauthButtons`.
        markSessionAlive();

        // On web, force a real page load instead of a client-side redirect.
        //
        // `refreshAuth()` used to run here and call `/iam/v4/iam/me` in-page
        // right after this exchange set the session cookie. Confirmed live
        // (Firefox, 2026-09-06): that request goes out with NO Cookie header
        // at all — not an expired/rejected cookie, the browser simply hasn't
        // attached the one just set by the exchange response to a fetch fired
        // this soon afterward, in this same page context. It answers a genuine
        // 401, `getSessionStatus` correctly reports that as `"signed-out"` (it
        // has no way to know this 401 is a timing artifact, not a real
        // sign-out), and the user bounces back to `/login` despite having a
        // valid session. A manual refresh right after works EVERY time — the
        // same `/iam/me` call, from a fresh page load, sends the cookie
        // correctly. So force exactly that instead of gambling on in-page
        // fetch timing: a hard navigation costs one extra page load but is the
        // one thing already proven to work.
        if (Platform.OS === "web") {
          window.location.href = "/";
          return;
        }

        // Native never reaches this screen (see the module doc above), but
        // keep the in-place refresh as a harmless fallback if that changes.
        await refreshAuth();
        setDone(true);
      })
      .catch((err: unknown) => {
        console.error("SSO callback exchange failed:", err);
        setError(t("ERROR_SSO_SIGNIN_FAILED"));
      });
  }, [params, t]);

  if (done) return <Redirect href="/" />;

  if (error) {
    return (
      <View className="flex-1 items-center justify-center gap-4 px-6">
        <Text className="text-center text-base">{error}</Text>
        <Pressable
          className="rounded-lg px-4 py-2"
          onPress={() => router.replace("/login")}
        >
          <Text className="text-center underline">Back to sign in</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View className="flex-1 items-center justify-center">
      <ActivityIndicator size="large" />
    </View>
  );
}
